'use client'

import React, { useRef, useState } from 'react'
import { type GameQuery, type MarketOutcome } from '@azuro-org/toolkit'
import cx from 'classnames'

import { Icon } from 'components/ui'

import { formatMarketOdds } from '@/Overtime/utils/markets'
import { OddsType } from '@/Overtime/enums/markets'
import OddsValue from '../OddsValue/OddsValue'
import type { TicketPosition } from '@/Overtime/types/markets'
import { sportMarketAsTicketPosition } from '@/Overtime/utils/marketsV2'
import { toast } from 'react-toastify'
import { useTicket } from '@/contexts/TicketContext'
import { useChainId } from 'wagmi'
import useOddsChange from 'src/hooks/useOddsChange'
import { sendGTMEvent } from '@next/third-parties/google'
import { PlainModal } from 'components/feedback'

type OvertimeOutcomeButtonProps = {
  marketName: string
  outcome: MarketOutcome
  game: NonNullable<GameQuery['game']>
  isGameOpen: boolean
  size?: 28 | 40
  section?: string
  isZeroOdd: boolean
  isCombiningFuturesNotSupported: (
    ticket: TicketPosition[],
    market: MarketOutcome
  ) => boolean
}

const OvertimeOutcomeButton: React.FC<any> = props => {
  const {
    marketName,
    outcome,
    isGameOpen,
    size = 28,
    section,
    positionText: selectionName,
    playerProps,
    isZeroOdd,
    market,
    fullMarket,
    position,
    isCombiningFuturesNotSupported,
  } = props

  const isGameRegularlyResolved =
    fullMarket.isResolved && !fullMarket.isCancelled

  const isWinner =
    isGameRegularlyResolved &&
    market.winningPositions &&
    market.winningPositions.includes(position)

  const chainId = useChainId()
  const { ticket, updateTicket, removeFromTicket } = useTicket()

  const isLocked = !isGameOpen
  const ticketPosition: TicketPosition = sportMarketAsTicketPosition(
    market,
    outcome.position,
    chainId
  )

  const isPlayerNameEmpty = playerProps && playerProps.playerName === ''

  const isActive = ticket.some(
    position =>
      position.gameId === ticketPosition.gameId &&
      position.typeId === ticketPosition.typeId &&
      position.position === ticketPosition.position &&
      position.line === ticketPosition.line &&
      (!isPlayerNameEmpty
        ? position.playerProps.playerId === ticketPosition.playerProps.playerId
        : true)
  )

  const nodeRef = useRef<HTMLDivElement>(null)

  // Add odds change effect for color/icon animation
  useOddsChange({ odds: outcome.odd, nodeRef })

  const buttonClassName = cx(
    'group/button w-full relative flex items-center justify-between ds:px-3 mb:px-2 overflow-hidden',
    'text-caption-13 font-medium border-none rounded-min select-none h-7',
    {
      'hover:text-brand-light-500 hover:bg-brand-900': !isLocked && !isActive,
      'text-black bg-brand-light-200': isActive,
      'bg-accent-grey-800': !isActive,
      'text-accent-grey-200 cursor-not-allowed': isLocked,
      'h-7': size === 28,
      'bg-result-button-won':
        !fullMarket.isCancelled &&
        isWinner &&
        fullMarket.isGameFinished &&
        !isZeroOdd,
      'bg-result-button-lost':
        !fullMarket.isCancelled &&
        !isWinner &&
        fullMarket.isGameFinished &&
        !isZeroOdd,
      'h-10': size === 40,
    }
  )

  //  const buttonClassName = cx(
  //    'w-full relative flex items-center justify-between px-3 overflow-hidden',
  //    'text-caption-13 font-semibold border-none rounded-min select-none',
  //    {
  //      'h-7': size === 28,
  //      'h-10': size === 40,
  //      'bg-result-button-won': !isCanceled && isWon,
  //      'bg-result-button-lost': !isCanceled && !isWon,
  //    }
  //  )

  const titleClassName = cx('text-left truncate', {
    'group-hover/button:text-brand-light-500': !isLocked && !isActive,
    'text-black': isActive,
    'text-accent-grey-200': !isActive || isLocked,
  })
  const oddsClassName = cx('group/odds flex items-center shrink-0 ml-3')
  const arrowClassName = cx(
    'size-4 text-transparent transition-color',
    'group-[.increased]/odds:text-accent-green-700',
    'group-[.decreased]/odds:text-accent-red-700 group-[.decreased]/odds:rotate-180'
  )
  const valueClassName = cx(
    'transition-color',
    'group-[.increased]/odds:text-accent-green-700',
    'group-[.decreased]/odds:text-accent-red-700'
  )

  const [showFuturesModal, setShowFuturesModal] = useState(false)

  const handleClick = () => {
    if (
      isCombiningFuturesNotSupported &&
      isCombiningFuturesNotSupported(ticket, market)
    ) {
      setShowFuturesModal(true)
      return
    }
    if (
      !ticketPosition.live &&
      (ticket.some(position => position.live) || (ticket.length && market.live))
    ) {
      // toast('Live positions only allowed as singles', {
      //   type: 'error',
      // })
      return
    }

    // Check if this position is already in the ticket
    const isAlreadyInTicket = ticket.some(
      position =>
        position.gameId === ticketPosition.gameId &&
        position.typeId === ticketPosition.typeId &&
        position.position === ticketPosition.position &&
        position.line === ticketPosition.line &&
        (!isPlayerNameEmpty
          ? position.playerProps.playerId ===
            ticketPosition.playerProps.playerId
          : true)
    )

    if (isAlreadyInTicket) {
      // Remove from ticket if already present
      removeFromTicket(ticketPosition)
      sendGTMEvent({
        event: 'odds_card_clicked',
        selected: false,
        section: section || (market.live ? 'Live' : 'Upcoming'),
        sports_name: market.sport.name,
        league_name: market.league.name,
        game_name: market.title,
        market_name: marketName,
        game_id: outcome.gameId,
      })
    } else {
      // Add to ticket if not present

      updateTicket({
        ...ticketPosition,
        market: {
          ...market,
          positionNames: market.positionNames || [],
          marketName: selectionName,
          displayPosition: position ?? market.displayPosition,
          tournamentName: market.tournamentName,
          odd: formatMarketOdds(OddsType.DECIMAL, outcome.odd),
        },
        position: outcome.position,
      })
      sendGTMEvent({
        event: 'odds_card_clicked',
        selected: true,
        section: section || (market.live ? 'Live' : 'Upcoming'),
        sports_name: market.sport.name,
        league_name: market.league.name,
        game_name: market.title,
        market_name: marketName,
        game_id: outcome.gameId,
      })
    }
  }

  return (
    <>
      <button
        className={buttonClassName}
        disabled={isLocked}
        onClick={handleClick}
      >
        <div className="flex items-center gap-1 min-w-0 flex-1">
          {isLocked && (
            <Icon
              className="shrink-0 size-4 text-accent-grey-400"
              name="interface/lock"
            />
          )}
          {!isPlayerNameEmpty && (
            <div className={titleClassName}>{playerProps.playerName}</div>
          )}
          <div className={titleClassName}>
            {!isPlayerNameEmpty ? `(${selectionName})` : selectionName}
          </div>
        </div>
        <div ref={nodeRef} className={oddsClassName}>
          <Icon className={arrowClassName} name="interface/caret_up" />
          <div>
            {isZeroOdd ? (
              fullMarket.isGameFinished ? (
                'Pending'
              ) : (
                '-'
              )
            ) : (
              <OddsValue
                className={valueClassName}
                odds={formatMarketOdds(OddsType.DECIMAL, outcome.odd)}
              />
            )}
          </div>
        </div>
      </button>
      {showFuturesModal && (
        <PlainModal
          closeModal={() => setShowFuturesModal(false)}
          overlayClosable
          withCloseButton
          containerClassName="backdrop-blur bg-accent-grey-980/10"
        >
          <div className="flex flex-col items-center justify-center min-h-[200px] w-full text-center">
            <img
              src="/images/illustrations/smile_sad.png"
              alt="info"
              className="w-16 h-16 mb-4"
            />
            <div className="text-caption-14 font-semibold mb-2">
              Combining futures on parlay is not supported
            </div>
            <button
              className="mt-6 w-full bg-accent-grey-800 text-white rounded-min py-2"
              onClick={() => setShowFuturesModal(false)}
            >
              OK
            </button>
          </div>
        </PlainModal>
      )}
    </>
  )
}

export default OvertimeOutcomeButton
